import React, { useMemo, useState, useEffect } from 'react';
import { Check, ArrowDownLeft, ArrowUpRight, AlertTriangle, RotateCcw } from 'lucide-react';
import { Modal, Pill, btn } from '../common/AdminUI';
import { allocate, money, dmy } from './reconApi';

/**
 * One bank line, put against the bills it pays off.
 *
 * Written once over a plain list of bills so that the credit side (our sales
 * invoices) and the debit side (PUR bills from the carrier) are the same screen
 * with a different list behind them — that is the whole reason step 7 is small.
 *
 * Nothing is guessed: the allocation is recomputed on every tick and the exact
 * outcome of each bill is shown before the accountant submits anything.
 */

const TONE = {
  credit: {
    pill: 'emerald', icon: ArrowDownLeft,
    head: 'Credit', verb: 'Settle credit',
    text: 'text-emerald-600 dark:text-emerald-400',
    bar: 'bg-emerald-500',
    btn: 'bg-emerald-600 hover:bg-emerald-700',
    noun: 'invoice',
  },
  debit: {
    pill: 'amber', icon: ArrowUpRight,
    head: 'Debit', verb: 'Settle debit',
    text: 'text-amber-600 dark:text-amber-400',
    bar: 'bg-amber-500',
    btn: 'bg-amber-600 hover:bg-amber-700',
    noun: 'PUR bill',
  },
};

const Figure = ({ label, value, tone = '', hint }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">{label}</p>
    <p className={`text-[21px] font-black leading-none mt-1.5 tabular-nums ${tone || 'text-slate-900 dark:text-white'}`}>
      ₹{money(value)}
    </p>
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

const SettleModal = ({
  open, onClose, side = 'credit', amount = 0,
  userName, bills = [], paid = [], loading = false, busy = false, onSubmit,
  onUndo, undoing = false,
}) => {
  const t = TONE[side] || TONE.credit;
  const [picked, setPicked] = useState([]);

  /* This screen does two opposite things — put money against open bills, and
     take a payment back — so they are two lists behind two tabs rather than
     one list under the other. Opening it lands on the open bills, which is
     what it is for; the other tab is there when something went wrong. */
  const [tab, setTab] = useState('open');
  const [takeBack, setTakeBack] = useState([]);

  // A fresh open starts clean; leaving the old selection behind would silently
  // carry one row's choices onto another row's money.
  useEffect(() => {
    if (!open) return;
    setPicked([]); setTab('open'); setTakeBack([]);
  }, [open, side, userName]);

  /* Money already spoken for cannot be spoken for twice: once the amount in
     hand is used up, the bills below the one it ran out inside can no longer
     be ticked. Unticking an earlier one frees it again. */
  const toggle = (id, allowed) => {
    if (!allowed) return;
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  // Allocation follows the order the bills are listed in (oldest first), not the
  // order they were clicked — otherwise the same ticks could settle differently.
  const chosen = useMemo(() => bills.filter((b) => picked.includes(b.id)), [bills, picked]);
  const spread = useMemo(() => allocate(amount, chosen), [amount, chosen]);

  const applied = spread.reduce((s, r) => s + r.applied, 0);
  const asked = spread.reduce((s, r) => s + r.owed, 0);
  const leftOver = Math.round((amount - applied) * 100) / 100;
  const shortBy = Math.round((asked - applied) * 100) / 100;
  const outcomeOf = (id) => spread.find((r) => r.id === id);

  const pct = amount > 0 ? Math.min(100, (applied / amount) * 100) : 0;

  // What is ticked on the other tab, and what it adds up to.
  const backChosen = useMemo(() => paid.filter((b) => takeBack.includes(b.id)), [paid, takeBack]);
  const backTotal = Math.round(backChosen.reduce((sum, b) => sum + b.amount, 0) * 100) / 100;
  const paidTotal = Math.round(paid.reduce((sum, b) => sum + b.amount, 0) * 100) / 100;
  const onPaid = tab === 'paid';

  const submit = () => {
    // The outcome travels with the amount: the server is told what the bill
    // becomes, and only this screen knows whether the money covered it.
    onSubmit?.(spread
      .filter((r) => r.applied > 0)
      .map((r) => ({ targetId: r.id, amount: r.applied, status: r.outcome, number: r.number })));
  };

  const selectTillSpent = () => {
    // Tick down the list until the money is used up — the common case, one click.
    let left = amount;
    const ids = [];
    for (const b of bills) {
      if (left <= 0) break;
      ids.push(b.id);
      left -= Math.max(0, (b.amount || 0) - (b.alreadyPaid || 0));
    }
    setPicked(ids);
  };

  const Tab = ({ id, label, count, on }) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      disabled={busy || undoing}
      className={`h-9 px-3.5 rounded-lg text-[12.5px] font-bold transition disabled:opacity-50
                  inline-flex items-center gap-2 whitespace-nowrap
                  ${on ? 'bg-white dark:bg-slate-900 shadow-sm text-slate-900 dark:text-white'
                       : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
    >
      {label}
      <span className={`px-1.5 py-0.5 rounded text-[11px] tabular-nums
                        ${on ? 'bg-slate-100 dark:bg-slate-800' : 'bg-slate-200/70 dark:bg-slate-800/70'}`}>
        {count}
      </span>
    </button>
  );

  return (
    <Modal
      open={open}
      onClose={busy || undoing ? () => {} : onClose}
      icon={t.icon}
      tone={t.pill}
      title={t.verb}
      subtitle={userName ? `${t.head} · ${userName}` : t.head}
      width="max-w-3xl"
      footer={(
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-[12px] text-slate-500 dark:text-slate-400 flex-1">
            {onPaid
              ? (backChosen.length === 0
                ? 'Tick what should not have been paid.'
                : `${backChosen.length} ${t.noun}${backChosen.length === 1 ? '' : 's'} will be due again.`)
              : (picked.length === 0
                ? `Pick the ${t.noun}s this money clears.`
                : `${spread.filter((r) => r.applied > 0).length} ${t.noun}${spread.filter((r) => r.applied > 0).length === 1 ? '' : 's'} will be updated.`)}
          </p>
          <div className="flex gap-2.5">
            <button type="button" onClick={onClose} disabled={busy || undoing}
              className={`${btn.quiet} disabled:opacity-50`}>
              Cancel
            </button>

            {onPaid ? (
              <button
                type="button"
                onClick={() => onUndo?.(backChosen)}
                disabled={undoing || backTotal <= 0}
                className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-xl text-sm font-bold
                           text-white transition disabled:opacity-40 disabled:cursor-not-allowed
                           bg-rose-600 hover:bg-rose-700"
              >
                <RotateCcw size={15} />
                {undoing ? 'Undoing…' : `Undo ₹${money(backTotal)}`}
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={busy || applied <= 0}
                className={`inline-flex items-center justify-center gap-2 h-10 px-5 rounded-xl text-sm font-bold
                            text-white transition disabled:opacity-40 disabled:cursor-not-allowed ${t.btn}`}
              >
                {busy ? 'Saving…' : `Settle ₹${money(applied)}`}
              </button>
            )}
          </div>
        </div>
      )}
    >
      {/* Running totals — the three numbers the accountant is actually watching. */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 p-4 mb-4">
        <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
          <Figure label="In hand" value={amount} tone={t.text} />
          {onPaid
            ? <Figure label="Taking back" value={backTotal} tone="text-rose-600 dark:text-rose-400"
                hint={backTotal > 0 ? 'comes back on this row' : 'nothing ticked'} />
            : <Figure label="Allocated" value={applied} />}
          {onPaid
            ? <Figure label="Paid so far" value={paidTotal} hint={`${paid.length} ${t.noun}${paid.length === 1 ? '' : 's'}`} />
            : shortBy > 0
              ? <Figure label="Short by" value={shortBy} tone="text-rose-600 dark:text-rose-400"
                  hint="stays due on the last bill" />
              : <Figure label="Left over" value={leftOver}
                  hint={leftOver > 0 ? 'stays on this row' : 'fully used'} />}
        </div>
        <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-800 mt-4 overflow-hidden">
          <div className={`h-full transition-all duration-300 ${onPaid ? 'bg-rose-500' : t.bar}`}
            style={{ width: `${onPaid ? (paidTotal > 0 ? Math.min(100, (backTotal / paidTotal) * 100) : 0) : pct}%` }} />
        </div>
      </div>

      {loading ? (
        <p className="py-12 text-center text-sm text-slate-400">Loading open {t.noun}s…</p>
      ) : bills.length === 0 && paid.length === 0 ? (
        <div className="py-12 text-center">
          <AlertTriangle size={30} className="mx-auto text-slate-300 dark:text-slate-600" />
          <p className="mt-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
            Nothing open for {userName || 'this user'}
          </p>
          <p className="mt-1 text-[12.5px] text-slate-400">
            There is no unpaid {t.noun} for this money to go against.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/60">
              <Tab id="open" label={`Open ${t.noun}s`} count={bills.length} on={!onPaid} />
              <Tab id="paid" label="Already paid" count={paid.length} on={onPaid} />
            </div>

            <div className="flex items-center gap-3">
              {onPaid ? (
                <>
                  {backChosen.length > 0 && (
                    <button type="button" onClick={() => setTakeBack([])} disabled={undoing}
                      className="text-[12px] font-bold text-slate-500 dark:text-slate-400 hover:underline disabled:opacity-50">
                      Clear {backChosen.length}
                    </button>
                  )}
                  {paid.length > 0 && (
                    <button type="button" onClick={() => setTakeBack(paid.map((b) => b.id))} disabled={undoing}
                      className="text-[12px] font-bold text-rose-600 dark:text-rose-400 hover:underline disabled:opacity-50">
                      Select all
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* A wrong tick is undone one at a time otherwise, and the list
                      disables the bills the money no longer reaches. */}
                  {picked.length > 0 && (
                    <button type="button" onClick={() => setPicked([])} disabled={busy}
                      className="text-[12px] font-bold text-slate-500 dark:text-slate-400 hover:underline
                                 disabled:opacity-50 disabled:no-underline">
                      Clear {picked.length}
                    </button>
                  )}
                  {bills.length > 0 && (
                    <button type="button" onClick={selectTillSpent} disabled={busy}
                      className="text-[12px] font-bold text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50">
                      Fill from the top
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {onPaid ? (
            paid.length === 0 ? (
              <p className="py-12 text-center text-[13px] text-slate-400">
                Nothing has been paid from this row yet.
              </p>
            ) : (
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                {paid.map((b, i) => {
                  const on = takeBack.includes(b.id);
                  return (
                    <label
                      key={b.id}
                      className={`flex items-start gap-3 px-4 py-3.5 cursor-pointer transition
                                  ${i > 0 ? 'border-t border-slate-100 dark:border-slate-800' : ''}
                                  ${on ? 'bg-rose-50/60 dark:bg-rose-500/[0.07]'
                                       : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`}
                    >
                      <span className={`mt-0.5 grid place-items-center w-[18px] h-[18px] rounded-[5px] border-2 flex-shrink-0 transition
                                        ${on ? 'bg-rose-600 border-rose-600 text-white'
                                             : 'border-slate-300 dark:border-slate-600'}`}>
                        {on && <Check size={12} strokeWidth={3.5} />}
                      </span>
                      <input type="checkbox" className="sr-only" checked={on} disabled={undoing}
                        onChange={() => setTakeBack((v) => (v.includes(b.id) ? v.filter((x) => x !== b.id) : [...v, b.id]))} />

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                          <span className="font-mono text-[13px] font-semibold text-slate-900 dark:text-white truncate max-w-[240px]"
                            title={b.number}>{b.number}</span>
                          <span className="text-[11.5px] text-slate-400 tabular-nums">{dmy(b.at)}</span>
                        </div>
                        <p className="text-[11.5px] text-slate-400 mt-0.5">payment #{b.paymentId}</p>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <p className="text-[13.5px] font-bold text-slate-900 dark:text-white tabular-nums">₹{money(b.amount)}</p>
                        {on && <p className="text-[11px] font-semibold text-rose-500 mt-1">comes back</p>}
                      </div>
                    </label>
                  );
                })}
              </div>
            )
          ) : bills.length === 0 ? (
            <p className="py-12 text-center text-[13px] text-slate-400">
              Every {t.noun} of theirs is paid. What was paid is under the other tab, and can be taken back.
            </p>
          ) : (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              {bills.map((b, i) => {
                const on = picked.includes(b.id);
                const out = outcomeOf(b.id);
                const owed = (b.amount || 0) - (b.alreadyPaid || 0);
                const spent = !on && leftOver <= 0.009;   // nothing left to give this one
                return (
                  <label
                    key={b.id}
                    className={`flex items-start gap-3 px-4 py-3.5 transition
                                ${i > 0 ? 'border-t border-slate-100 dark:border-slate-800' : ''}
                                ${spent ? 'cursor-not-allowed opacity-45' : 'cursor-pointer'}
                                ${on ? 'bg-blue-50/50 dark:bg-blue-500/[0.07]'
                                     : spent ? '' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`}
                  >
                    <span className={`mt-0.5 grid place-items-center w-[18px] h-[18px] rounded-[5px] border-2 flex-shrink-0 transition
                                      ${on ? 'bg-blue-600 border-blue-600 text-white'
                                           : spent ? 'border-slate-200 dark:border-slate-700' : 'border-slate-300 dark:border-slate-600'}`}>
                      {on && <Check size={12} strokeWidth={3.5} />}
                    </span>
                    <input type="checkbox" className="sr-only" checked={on} disabled={spent}
                      onChange={() => toggle(b.id, !spent)} />

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                        <span className="font-mono text-[13px] font-semibold text-slate-900 dark:text-white">{b.number}</span>
                        <span className="text-[11.5px] text-slate-400 tabular-nums">{dmy(b.date)}</span>
                      </div>
                      {b.note && <p className="text-[11.5px] text-slate-400 mt-0.5">{b.note}</p>}
                      {b.alreadyPaid > 0 && (
                        <p className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5 tabular-nums">
                          ₹{money(b.alreadyPaid)} already paid of ₹{money(b.amount)}
                        </p>
                      )}
                    </div>

                    <div className="text-right flex-shrink-0">
                      <p className="text-[13.5px] font-bold text-slate-900 dark:text-white tabular-nums">₹{money(owed)}</p>
                      {on && out && out.applied > 0 && (
                        <div className="mt-1.5 flex flex-col items-end gap-1">
                          <Pill tone={out.outcome === 'paid' ? 'emerald' : 'amber'}>
                            {out.outcome === 'paid' ? 'Paid' : 'Partially paid'}
                          </Pill>
                          {out.stillDue > 0 && (
                            <span className="text-[11px] text-rose-500 font-semibold tabular-nums">
                              ₹{money(out.stillDue)} stays due
                            </span>
                          )}
                        </div>
                      )}
                      {spent && (
                        <p className="text-[11px] text-slate-400 mt-1.5">nothing left</p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {!onPaid && shortBy > 0 && (
            <p className="mt-3 text-[12.5px] text-slate-500 dark:text-slate-400 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <span>
                The ticked {t.noun}s come to ₹{money(asked)} but only ₹{money(amount)} is in hand.
                The last one takes what is left and keeps ₹{money(shortBy)} due.
              </span>
            </p>
          )}
        </>
      )}
    </Modal>
  );
};

export default SettleModal;
